import type { UserPermissionFlag } from '~~/entities/user'
import { Role } from '~~/entities/user'

export const ADMIN_HOME_URL = '/admin/decks'
export const USER_PROFILE_URL = '/user/profile'
export const DEFAULT_FALLBACK_URL = '/meetings'

export default defineNuxtRouteMiddleware(async (to, _from) => {
  if (import.meta.server) return

  const { state, hasRole, hasPermission, userHasCompanies, hasFlag, homepageRoute } = useUser()
  const { selectedCompany } = useCompanies()
  const isAdmin = hasRole(Role.SuperAdmin) || userHasCompanies.value

  if (to.path === '/user/profile' && isAdmin) {
    return navigateTo('/admin/profile', { replace: true })
  }

  if (to.path === '/meetings' && isAdmin) {
    return navigateTo({ path: '/admin/meetings', query: to.query }, { replace: true })
  }

  if (!to.meta.requireRoles?.length && !to.meta.requireFlags?.length && !to.meta.requirePermissions?.length) {
    return
  }

  if (!state.value.user && to.path !== '/login') {
    return navigateTo({ path: `/login`, replace: true, query: { redirectedFrom: window.location.href } })
  }

  if (to.meta.requiresSelectedCompany && selectedCompany.value == null) {
    return navigateTo('/admin/companies')
  }

  return checkProtectedRoutes({ to, hasRole, hasPermission, hasFlag, homepageRoute })
})

function checkProtectedRoutes({
  to,
  hasRole,
  hasPermission,
  hasFlag,
  homepageRoute,
}: {
  to: ReturnType<typeof useRoute>
  hasRole: (...roles: Role[]) => boolean
  hasPermission: (...permissions: UserPermissionFlag[]) => boolean
  hasFlag: (...flags: string[]) => boolean
  homepageRoute: ComputedRef<string>
}) {
  if (to.meta.requireRoles?.length) {
    if (!hasRole(...to.meta.requireRoles)) {
      if (to.meta.onProtectedRouteRedirect) {
        to.meta.onProtectedRouteRedirect()
      }
      return navigateTo(to.meta.rolesRedirect ?? to.meta.redirectTo ?? USER_PROFILE_URL, { replace: true })
    }
  }

  if (to.meta.requireFlags?.length) {
    if (!hasFlag(...to.meta.requireFlags)) {
      if (to.meta.onProtectedRouteRedirect) {
        to.meta.onProtectedRouteRedirect()
      }
      return navigateTo(to.meta.flagsRedirect ?? to.meta.redirectTo ?? homepageRoute.value, { replace: true })
    }
  }

  if (to.meta.requirePermissions?.length) {
    if (!hasPermission(...to.meta.requirePermissions)) {
      if (to.meta.onProtectedRouteRedirect) {
        to.meta.onProtectedRouteRedirect()
      }
      return navigateTo(to.meta.permissionsRedirect ?? to.meta.redirectTo ?? DEFAULT_FALLBACK_URL, { replace: true })
    }
  }
}
